import React, {useEffect, useState, useRef, useCallback} from 'react';
import {Box, VStack, Input, InputGroup, InputRightElement, Flex, useTheme, Spinner} from '@chakra-ui/react';
import {TextG12, TextG14, TextG24} from '../../components/typography';
import {fetchUserHandleAvailable} from '../../endpoints/api';
import {StyledCheckCircleFilled} from '../../constants/eventOptions';
import Icon from '../../components/Icon';
import {uniqueNamesGenerator, colors, NumberDictionary} from 'unique-names-generator';
import useAuth from '../../hooks/useAuth';
import animals from '../../constants/animals';
import logger from '../../utils/logger';

interface Intro8UsernameProps {
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (username: string) => void;
  initialUsername?: string;
}

const IntroUsername = ({onValidationChange, onDataChange, initialUsername}: Intro8UsernameProps) => {
  const theme = useTheme();
  const {user} = useAuth();
  const numberDictionary = NumberDictionary.generate({min: 100, max: 999});
  
  const [userName, setUserName] = useState<string>(
    initialUsername ||
    user?.handle ||
    uniqueNamesGenerator({
      dictionaries: [colors, animals, numberDictionary],
      length: 3,
      separator: '',
      style: 'capital',
    })
  );
  const [inputFocused, setInputFocused] = useState(false);
  const [userNameError, setUserNameError] = useState<string>('initial');
  const [isChecking, setIsChecking] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced username availability check (CT-5853 fix)
  useEffect(() => {
    // Clear any pending timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (userName.length > 2 && userName !== user?.handle) {
      setIsChecking(true);
      logger.debug('IntroUsername: Starting availability check', 'api_call', {userName});
      
      // Debounce the API call by 300ms
      debounceTimerRef.current = setTimeout(() => {
        fetchUserHandleAvailable(userName)
          .then(() => {
            logger.debug('IntroUsername: Handle available', 'api_call', {userName});
            setUserNameError('');
            setIsChecking(false);
          })
          .catch((e) => {
            logger.debug('IntroUsername: Handle unavailable', 'api_call', {userName, error: e.message});
            setUserNameError(e.message);
            setIsChecking(false);
          });
      }, 300);
    } else {
      setUserNameError('initial');
      setIsChecking(false);
    }

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [userName, user?.handle]);

  const isValid = userName.length > 2 && !isChecking && (!userNameError || userNameError === 'initial' || userName === user?.handle);

  // Diagnostic logging for validation state (CT-5853)
  React.useEffect(() => {
    logger.debug('IntroUsername validation', 'user_action', {
      userName,
      userNameError,
      isChecking,
      isValid,
    });
    onValidationChange(isValid);
    onDataChange(userName);
  }, [isValid, userName, isChecking, onValidationChange, onDataChange]);

  return (
    <VStack
      flex={1}
      spacing={6}
      px={4}
      pt={4}
      pb={4}
      align="stretch"
      justify="flex-start"
      overflow="auto">
      {/* Title */}
      <Box>
        <TextG24 fontSize="26px" fontWeight="700" lineHeight="34px" color="#262626">
          Choose How You'll
        </TextG24>
        <TextG24 fontSize="26px" fontWeight="700" lineHeight="34px" color="#C9136B">
          Show Up
        </TextG24>
      </Box>

      {/* Description */}
      <TextG14 lineHeight="22px" color="#636363" fontWeight="500">
        This is the name other members will see when you chat, RSVP to events, and connect in
        the community.
      </TextG14>

      <TextG14 lineHeight="22px" color="#262626" fontWeight="700">
        We've suggested a fun nickname, but feel free to make it your own!
      </TextG14>

      {/* Username Input */}
      <Box>
        <InputGroup>
          <Input
            maxLength={40}
            onBlur={() => setInputFocused(false)}
            onFocus={() => setInputFocused(true)}
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            size="lg"
            borderRadius="12px"
            border="2px solid"
            borderColor={theme.colors.gray[900]}
            _focus={{
              borderColor: theme.colors.gray[900],
              boxShadow: 'none',
            }}
            fontSize="16px"
            py={6}
            pr={20}
            placeholder="Enter a nickname"
          />
          {!inputFocused && (
            <InputRightElement mr={4} w={14} h={14} pointerEvents="none">
              <TextG12 color="blue">Change</TextG12>
            </InputRightElement>
          )}
        </InputGroup>
        
        {/* Validation Messages */}
        {isChecking && userName.length > 2 ? (
          <Flex gap={2} mt="8px" align="center">
            <Spinner size="sm" color={theme.colors.gray[500]} />
            <TextG14 color={theme.colors.gray[500]}>Checking availability...</TextG14>
          </Flex>
        ) : userName.length > 2 && !userNameError ? (
          <Flex gap={1} mt="8px">
            <StyledCheckCircleFilled
              style={{
                fontSize: '18px',
                height: '18px',
                width: '18px',
                color: 'green',
                fill: 'green',
              }}
            />
            <TextG14 color="green">Available</TextG14>
          </Flex>
        ) : userName !== user?.handle && userNameError && userNameError !== 'initial' ? (
          <Flex gap={1} mt="8px">
            <Icon
              iconName={'fi fi-rr-cross-circle'}
              style={{
                fontSize: '18px',
                height: '18px',
                width: '18px',
                color: 'red',
                fill: 'red',
              }}
            />
            <TextG14 color="red">{userNameError}</TextG14>
          </Flex>
        ) : null}
      </Box>
    </VStack>
  );
};

export default IntroUsername;
